import React, { useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { Send, Mail, MapPin, Linkedin } from 'lucide-react'
import Button from './Button'
import Card from './Card'

const Contact = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })

  const [status, setStatus] = useState('')
  const [statusMessage, setStatusMessage] = useState('')

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('sending')
    setStatusMessage('Sending message...')

    try {
      const payload = new FormData()
      payload.append('name', formData.name)
      payload.append('email', formData.email)
      payload.append('subject', formData.subject)
      payload.append('message', formData.message)
      payload.append('_replyto', formData.email)
      payload.append('_subject', formData.subject)
      payload.append('_captcha', 'false')
      payload.append('_template', 'box')

      const response = await fetch('https://formsubmit.co/ajax/25c4e3549642cd25efefb17085bca429', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: payload,
      })

      if (!response.ok) {
        const errorBody = await response.text()
        throw new Error(`Failed to send message: ${errorBody}`)
      }

      const result = await response.json()
      console.log('FormSubmit response:', result)

      const isSuccess =
        typeof result.success === 'boolean'
          ? result.success
          : String(result.success).toLowerCase() === 'true'

      if (isSuccess) {
        setStatus('success')
        setStatusMessage('Message sent successfully!')
        setFormData({ name: '', email: '', subject: '', message: '' })
      } else {
        setStatus('error')
        setStatusMessage(result.message || 'Unexpected response from FormSubmit.')
      }
    } catch (error) {
      console.error('Error sending contact form:', error)
      setStatus('error')
      setStatusMessage(error.message || 'Something went wrong. Please try again.')
    } finally {
      setTimeout(() => setStatus(''), 4000)
      setTimeout(() => setStatusMessage(''), 4000)
    }
  }

  return (
    <section
      id="contact"
      ref={ref}
      className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-navy-dark"
    >
      <div className="max-w-5xl mx-auto">
        <div
          className={`transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-dark dark:text-navy-lightest mb-4">
              Get In Touch
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-navy-medium to-navy-deep dark:from-github-border dark:to-github-border mx-auto mb-4"></div>
            <p className="text-xl text-navy-base dark:text-navy-soft max-w-2xl mx-auto">
              I'm always open to new opportunities and collaborations. Let's create something amazing together!
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-8">
            {/* Contact Info */}
            <div className="md:col-span-2 space-y-6">
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-navy-medium/10 rounded-lg">
                    <Mail className="text-navy-medium" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-dark dark:text-navy-lightest mb-1">
                      Email
                    </h3>
                    <a
                      href="mailto:chamikascp@gmail.com"
                      className="text-navy-base dark:text-navy-soft hover:text-accent transition-colors"
                    >
                      chamikascp@gmail.com
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-navy-medium/10 rounded-lg">
                    <MapPin className="text-navy-medium" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-dark dark:text-navy-lightest mb-1">
                      Location
                    </h3>
                    <p className="text-navy-base dark:text-navy-soft">
                      Bilding Kotasa, Ethimale Wewa<br />
                      Monaragala, Sri Lanka
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-navy-medium/10 rounded-lg">
                    <Linkedin className="text-navy-medium" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-dark dark:text-navy-lightest mb-1">
                      LinkedIn
                    </h3>
                    <a
                      href="https://www.linkedin.com/in/shehan-chamika-334562383"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-navy-base dark:text-navy-soft hover:text-accent transition-colors"
                    >
                      /in/shehan-chamika
                    </a>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-3">
              <Card className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-navy-base dark:text-navy-soft mb-2"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-navy-pale dark:bg-github-bg border border-navy-soft dark:border-github-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors text-navy-dark dark:text-github-text"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-navy-base dark:text-navy-soft mb-2"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-navy-pale dark:bg-github-bg border border-navy-soft dark:border-github-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors text-navy-dark dark:text-github-text"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-navy-base dark:text-navy-soft mb-2"
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-navy-pale dark:bg-github-bg border border-navy-soft dark:border-github-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors text-navy-dark dark:text-github-text"
                      placeholder="Project Inquiry"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-navy-base dark:text-navy-soft mb-2"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 bg-navy-pale dark:bg-github-bg border border-navy-soft dark:border-github-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors text-navy-dark dark:text-github-text resize-none"
                      placeholder="Tell me about your project..."
                    ></textarea>
                  </div>

                  <Button type="submit" variant="primary" size="lg" className="w-full">
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                    <Send size={20} className="ml-2" />
                  </Button>

                  {status && (
                    <p
                      className={`text-center ${
                        status === 'success'
                          ? 'text-mint dark:text-mint'
                          : status === 'error'
                          ? 'text-red-500 dark:text-red-400'
                          : 'text-navy-base dark:text-navy-soft'
                      }`}
                    >
                      {statusMessage}
                    </p>
                  )}
                </form>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact